import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { TasksQuery$variables } from '../../src/graphql/__generated__/TasksQuery.graphql';
import { TIMEOUT_FOR_FILTER_TABLE } from '../const';
import { combineFilterFlexible } from '../functions';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import useGetFilter from '../hooks/useGetFilter';
import { IGraphQLTableLoader } from '../hooks/useGraphQLLoader';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import { FilterTableFieldParamType } from '../types';
import TableDbLoader from './tableDbLoader';

export interface LoaderVarsType {
    filterFlexible?: string;
    orderBy?: string[];
}

const WrapperTableDBLoader = <T extends TasksQuery$variables, K, E>({
    query,
    fragmentList,
    fragment,
    fragmentConnection,
    nameQuery,
    columnsLabel,
    paramSorter,
    isAddColumnAction = false,
    onUpdateData,
    extendRow,
    extendRowByField,
    paramFilter,
    varRequest,
    sizeOfPage,
    externalFetchKey,
    fixedColumn,
    setVars,
}: IGraphQLTableLoader<T, K, E> & {
    isAddColumnAction?: boolean;
    setVars: (newVars: LoaderVarsType) => void;
}) => {
    const { prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const page = useDynamicSelector('table', 'page');
    const pageSize = useDynamicSelector('table', 'pageSize');
    const sorter = useDynamicSelector('table', 'sorter');

    const listGroupFilters = useDynamicSelector('table', 'listGroupFilters');
    const prefixFetchKey = useDynamicSelector('table', 'prefixFetchKey');

    const filterFlexible = useGetFilter(paramFilter as { [key: string]: FilterTableFieldParamType });

    const [currentFilter, setCurrentFilter] = useState(filterFlexible);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [firstLoad, setFirstLoad] = useState<boolean>(true);

    useEffect(() => {
        setFirstLoad(false);
    }, []);

    useEffect(() => {
        if (currentFilter && prefixFetchKey) {
            setterTable({
                prefixFetchKey: uuidv4(),
            });
        }
    }, [currentFilter]);

    useEffect(() => {
        if (timeoutRef?.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (Object.keys(listGroupFilters).length) {
                if (filterFlexible) {
                    setCurrentFilter(filterFlexible);
                }
            } else {
                setCurrentFilter(null);
            }
        }, TIMEOUT_FOR_FILTER_TABLE);
    }, [filterFlexible]);

    const [firstSizeOfPage] = sizeOfPage || [];

    const getActualOrderBy = () => {
        if (sorter.length) {
            return sorter;
        }
        if (varRequest?.order_by) {
            return varRequest.order_by;
        }
        return [];
    };

    useEffect(() => {
        setVars({
            filterFlexible: combineFilterFlexible(currentFilter, varRequest?.filter_flexible),
            orderBy: [...getActualOrderBy().filter((val) => val)] as string[],
        });
    }, [varRequest?.filter_flexible, currentFilter, varRequest?.order_by]);

    return (
        <TableDbLoader<T, K, E>
            query={query}
            fragmentList={fragmentList}
            fragment={fragment}
            fragmentConnection={fragmentConnection}
            nameQuery={nameQuery}
            columnsLabel={columnsLabel}
            paramSorter={paramSorter}
            onUpdateData={onUpdateData}
            isAddColumnAction={isAddColumnAction}
            varRequest={
                {
                    ...varRequest,
                    page,
                    first: firstLoad ? +firstSizeOfPage : pageSize,
                    order_by: getActualOrderBy(),
                    filter_flexible: combineFilterFlexible(currentFilter, varRequest?.filter_flexible),
                } as any as T
            }
            extendRow={extendRow}
            extendRowByField={extendRowByField}
            externalFetchKey={externalFetchKey}
            fixedColumn={fixedColumn}
        />
    );
};

export default WrapperTableDBLoader;
